import { Clipboard } from '@angular/cdk/clipboard';
import { CurrencyPipe } from '@angular/common';
import { Component, EventEmitter, inject, OnDestroy, OnInit, Output, signal, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { ClicktermDialog } from '@clickterm/widget';
import {
  CellClickedEvent,
  ColDef,
  GetContextMenuItemsParams,
  GetRowIdFunc,
  GetRowIdParams,
  GridReadyEvent,
  ICellRendererParams,
  MenuItemDef,
  SelectionChangedEvent,
  ValueGetterParams,
} from 'ag-grid-community';
import { debounceTime, interval, Subject, Subscription, takeUntil, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AGTableBase } from '../../shared/components/table/ag-table-base';
import { AuthUser, Role, UserAdmin, UserSearchEvent } from '../../shared/models/user.model';
import { BrowserUtils } from '../../shared/services/browser-utils';
import { ClicktermService } from '../../shared/services/clickterm.service';
import { LegalDocumentsService } from '../../shared/services/legal-documents.service';
import { LocalStorageService } from '../../shared/services/localStorage.service';
import { DialogRef, ModalService } from '../../shared/services/modal.service';
import { NotificationService } from '../../shared/services/notification.service';
import { UserAgreementUtils } from '../../shared/services/user-agreement-utils';
import { UserActionData, UsersRequestParams, UsersService } from '../../shared/services/users.service';
import { createBalanceColumn, createLastActiveColumn, usersAccountColumns } from './users-account-columns';
import { lastTestRequestedColumn } from './last-test-requested-column';
import { UsersActionsComponent } from './users-actions/users-actions.component';
declare const moment: (input?: string | number | Date | null | undefined) => {
  format: (pattern: string) => string;
};

@Component({
  standalone: false,
  selector: 'app-users-table',
  templateUrl: 'users-table.component.html',
  styleUrls: ['./users-table.component.scss'],
})
export class UsersTableComponent extends AGTableBase implements OnInit, OnDestroy {
  @ViewChild('exportModalTpl', { read: TemplateRef, static: false }) exportModalTpl: TemplateRef<unknown>;
  @ViewChild('deleteModalTpl', { read: TemplateRef, static: false }) deleteModalTpl: TemplateRef<unknown>;
  @ViewChild('subaccountModalTpl', { read: TemplateRef, static: false }) subaccountModalTpl: TemplateRef<unknown>;
  @ViewChild('commentModalTpl', { read: TemplateRef, static: false }) commentModalTpl: TemplateRef<unknown>;
  @ViewChild('balanceAdjustmentModalTpl', { read: TemplateRef, static: false }) balanceAdjustmentModalTpl: TemplateRef<unknown>;
  @Output() actions = new EventEmitter<UserActionData>();
  @Output() changeBatch = new EventEmitter<UserAdmin[]>();

  isAdmin = false;
  onAfterDelete = new EventEmitter();
  messageForModal: string;
  storageContextName = 'users-table';

  requestParams = new UsersRequestParams();
  userSearchObject: UserSearchEvent = null;

  commentTextFormControl: FormControl = new FormControl();
  commentModalDialogRef: DialogRef;
  balanceAdjustmentForm = new FormGroup({
    operation: new FormControl('ADJUST'),
    amountEuro: new FormControl(null),
  });
  balanceAdjustmentModalDialogRef: DialogRef;

  MAX_TOTAL_ITEMS_COUNT = 5000;
  loading = false;
  user: AuthUser;
  currentRow: UserAdmin;
  private destroy$ = new Subject<void>();
  private usersRequestSubscription?: Subscription;
  private readonly clicktermService = inject(ClicktermService);

  batchSelectedIds = new Set<number>();

  // signals
  readonly isAgreementDownloading = signal<boolean>(false);

  // ng grid
  columnDefs: ColDef[] = [
    {
      headerName: '',
      field: 'batch',
      checkboxSelection: true,
      maxWidth: 40,
      headerCheckboxSelection: true,
      pinned: 'left',
      lockPinned: true,
      lockPosition: 'left',
      lockVisible: true,
      suppressColumnsToolPanel: true,
      suppressMenu: true,
      suppressMovable: true,
    },
    {
      headerName: 'ID',
      field: 'id',
      maxWidth: 85,
      minWidth: 75,
      sortable: true,
      comparator: () => 0,
      initialSort: 'desc',
      sortingOrder: ['desc', 'asc', null],
      cellRenderer: (params: ICellRendererParams) => {
        const _ = params.data;
        return `<div class="one-line">${_.id}</div>`;
      },
    },
    {
      headerName: 'Role/Email',
      field: 'username',
      flex: 1.1,
      cellRenderer: (params: ICellRendererParams) => {
        const _: UserAdmin = params.data;
        return `
                    <div class="d-flex justify-content-start align-items-center gap-0_5">
                        <span class="d-flex me-0_8" title="${_.role}">${this.getIconForRole(_)}</span>
                        <div class="d-flex flex-column justify-content-center align-items-start">
                            <span class="one-line">${_.email}</span>
                            <span class="one-line">(${_.username})</span>
                        <div>
                    </div>
                `;
      },
    },
    usersAccountColumns.externalUserId,
    {
      headerName: 'Linked accounts',
      field: 'parentEmail',
      flex: 0.8,
      cellRenderer: (params: ICellRendererParams) => {
        const _ = params.data;
        if (!_.parentEmail) {
          return `<div class="one-line clickable"><span>Subaccounts: ${_.countSubaccounts}</span></div>`;
        }
        return _.parentEmail
          ? `<div class="two-lines clickable"><span>Parent: ${_.parentEmail}</span></div>`
          : `<div class="one-line">Parent not found</div>`;
      },
      onCellClicked: (event: CellClickedEvent) => this.onAccountClick(event),
    },
    usersAccountColumns.company,
    usersAccountColumns.department,
    createLastActiveColumn(params => this.formatLastLogin(params)),
    createBalanceColumn(user => this.formatCurrency(user)),
    usersAccountColumns.lastTopUp,
    lastTestRequestedColumn,
    {
      headerName: 'Owner',
      field: 'ownerUserEmail',
      flex: 0.4,
      headerTooltip: 'Account manager',
      cellRenderer: (params: ICellRendererParams) => {
        const _ = params.data;
        return `<div class="one-line">${this.getOwnerUsername(_)}</div>`;
      },
    },
    {
      headerName: 'Agreement accepted',
      colId: 'agreementStatus',
      valueGetter: (params: ValueGetterParams) => this.getAgreementStatusText(params.data),
      maxWidth: 200,
      minWidth: 120,
      flex: 0.4,
      cellClass: 'justify-content-center',
      headerTooltip: 'Shows whether clickterm/custom agreement is marked as accepted',
      cellRenderer: (params: ICellRendererParams) => {
        return this.renderAgreementStatus(params.data);
      },
    },
    {
      headerName: 'Status',
      colId: 'emailVerified',
      maxWidth: 160,
      minWidth: 160,
      cellClass: 'justify-content-center',
      flex: 0.35,
      headerTooltip: 'Email verification and HMAC-based OTP status',
      cellRenderer: (params: ICellRendererParams) => {
        const user: UserAdmin = params.data;
        const companyDetailsVerifiedHtml =
          user.role == Role.MAIN
            ? `<i title="${user.companyDetailsVerified ? 'Company details are verified' : 'Company details are not verified'}"  class="fs-3 me-0_5 ${user.companyDetailsVerified ? 'icon-company-details-verified' : 'icon-company-details-unverified'}"></i>`
            : '';
        const emailVerifiedHtml = `<i title="${user.emailVerified ? 'Email verified' : 'Email is not verified'}" class="far fa-envelope fs-3 me-0_5 ${user.emailVerified ? 'text-success-s2' : 'text-error-r3'}"></i>`;
        const mfaEnabledHtml = `<i title="HMAC-based OTP is ${user.mfaEnabled ? 'enabled' : 'disabled'}" class="fas fa-mobile-alt fs-3 me-0_5 ${user.mfaEnabled ? 'text-success-s2' : 'text-error-r3'}"></i>`;

        return `<div class="one-line text-center d-flex justify-content-end align-items-center gap-15">
                            ${companyDetailsVerifiedHtml} ${UserAgreementUtils.renderAgreement(user)} ${emailVerifiedHtml} ${mfaEnabledHtml}
                        </div>`;
      },
    },
    {
      headerName: 'Enabled',
      field: 'enabled',
      sortable: true,
      comparator: () => 0,
      initialSort: null,
      sortingOrder: ['desc', 'asc', null],
      cellClass: 'justify-content-center',
      flex: 0.3,
      cellRenderer: (params: ICellRendererParams) => {
        const isChecked = params.data.enabled ? 'checked' : '';
        return `<div class="form-check form-switch d-flex justify-content-center">
                            <input type="checkbox" class="form-check-input medium" ${isChecked}/>
                        </div>`;
      },
      onCellClicked: (event: CellClickedEvent) => this.onEnableOrDisableUser(event),
    },
    usersAccountColumns.paymentType,
    usersAccountColumns.rankingIndex,
    {
      headerName: 'Actions',
      field: 'actions',
      maxWidth: 100,
      minWidth: 100,
      pinned: 'right',
      lockPinned: true,
      lockPosition: 'right',
      lockVisible: true,
      suppressColumnsToolPanel: false,
      suppressMenu: false,
      suppressAutoSize: true,
      tooltipField: 'actions',
      headerTooltip: 'Right click on a row to see more options.',
      headerClass: 'action-cell',
      cellClass: 'action-cell',
      cellRenderer: UsersActionsComponent,
      cellRendererParams: () => ({
        canAdjustBalance: this.canAdjustBalance(),
        onAction: (clickedItem: string, row: UserAdmin) => this.onAction({ name: clickedItem, row, column: 'action' }),
      }),
      onCellClicked: (event: CellClickedEvent) => this.onActionClick(event),
    },
  ];

  rowData!: UserAdmin[];
  // ng grid

  constructor(
    public notificationService: NotificationService,
    public service: UsersService,
    private legalDocumentsService: LegalDocumentsService,
    public router: Router,
    public modal: ModalService,
    private currencyPipe: CurrencyPipe,
    private localStorage: LocalStorageService,
    private clipboard: Clipboard
  ) {
    super();
  }

  ngOnInit() {
    this.service.getAuthUser().then(user => {
      this.user = user;
      this.paginationPageSize = this.localStorage.get(`users_table_component_size_${user.id}`) || 20;
      if (this.gridApi) {
        // The grid was ready before the user loaded, so apply the saved settings now.
        this.restoreColumnState();
        this.update();
      }
      this.gridApi?.refreshCells({ columns: ['actions'], force: true });
    });
    this.actionSubscription = this.service.action$.subscribe(action => {
      this.onAction(action);
    });
    // The grid can still emit column events while it is being torn down (e.g. the layout
    // changes when impersonating), so never save a state read from a destroyed grid.
    this.columnChange$.pipe(debounceTime(1000), takeUntil(this.destroy$)).subscribe(() => {
      if (!this.user || !this.gridApi || this.gridApi.isDestroyed()) {
        return;
      }
      const currentColumnState = this.gridApi.getColumnState();
      if (!currentColumnState?.length) {
        return;
      }
      this.localStorage.set(`users_table_state_${this.user.id}`, currentColumnState);
    });
    interval(30_000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.update(false));
  }

  onAction(event: UserActionData) {
    this.currentRow = event.row;
    this.actions.emit(event);
    if (event.name === 'enableClicktermAgreements') {
      this.onEnableClicktermAgreements(event.row);
    }

    if (event.name === 'enableCustomAgreement') {
      this.onEnableCustomAgreement(event.row);
    }

    if (event.name === 'delete') {
      this.messageForModal = `You are going to delete "${event.row.email}". You won't be able to revert this!`;
      const dialogRef = this.modal.alert().component(this.deleteModalTpl).open();
      dialogRef.result.then(result => {
        if (result) {
          this.delete(event.row);
        }
      });
    }

    if (event.name === 'login') {
      this.loading = true;
      this.service.impersonate(event.row.id).subscribe({
        next: () => {
          this.router.navigate(['/dashboard']);
        },
        error: error => {
          this.notificationService.error({
            title: 'Users',
            message: error.message ? error.message : 'An error occurred while creating the token',
            requestMessage: error.statusText,
            requestCode: error.status,
            ts: error.timestamp ? error.timestamp : null,
          });
          this.loading = false;
        },
      });
    }

    if (event.name === 'verifyCompanyDetails') {
      this.messageForModal = `You are going to mark company details for this account as verified.`;
      const dialogRef = this.modal.alert().component(this.deleteModalTpl).open();
      dialogRef.result.then(result => {
        if (result) {
          this.verifyCompanyDetails(event.row);
        }
      });
    }

    if (event.name === 'resendVerificationEmail') {
      this.messageForModal = `You are going to resend verification to "${event.row.email}". You won't be able to revert this!`;
      const dialogRef = this.modal.alert().component(this.deleteModalTpl).open();
      dialogRef.result.then(result => {
        if (result) {
          this.resendVerificationEmail(event.row);
        }
      });
    }

    if (event.name === 'mfaDisable') {
      this.messageForModal = `You are going to disable 2FA for "${event.row.email}". You won't be able to revert this!`;
      const dialogRef = this.modal.alert().component(this.deleteModalTpl).open();
      dialogRef.result.then(result => {
        if (result) {
          this.mfaDisable(event.row);
        }
      });
    }

    if (event.name === 'comment') {
      this.commentTextFormControl.setValue(event.row.comment);
      this.commentModalDialogRef = this.modal.alert().size('sm').component(this.commentModalTpl).open();
    }

    if (event.name === 'adjustBalance') {
      this.balanceAdjustmentForm.reset({ operation: 'ADJUST', amountEuro: null });
      this.balanceAdjustmentModalDialogRef = this.modal.alert().size('sm').component(this.balanceAdjustmentModalTpl).open();
    }

    if (event.name === 'downloadAgreement') {
      this.downloadAgreement(event.row);
    }

    if (event.name === 'viewMsaDpaAgreement') {
      this.viewMsaDpaAgreement(event.row);
    }
  }

  public canAdjustBalance(): boolean {
    return this.user?.id != null && environment.showInvoiceDeleteToUserIds.has(this.user.id);
  }

  private onAccountClick(event: CellClickedEvent): void {
    this.actions.emit({ name: 'accountClick', row: event.data, column: 'account' });
  }

  private onEnableOrDisableUser(event: CellClickedEvent): void {
    if ((event.event?.target as HTMLElement).classList.contains('form-check-input')) {
      this.loading = true;
      const user: UserAdmin = event.data;
      user.enabled = !user.enabled;
      this.service.updateEnable(event.data.id, user.enabled).subscribe({
        next: () => {
          this.notificationService.success('User updated', 'Users');
          this.update();
        },
        error: () => {
          this.notificationService.error('User updating an error', 'Users');
        },
      });
    }
  }

  private verifyCompanyDetails(user: UserAdmin) {
    this.loading = true;
    this.service.verifyCompanyDetails(user.id).subscribe({
      next: () => {
        this.notificationService.success('Company details verified', 'Users');
        user.companyDetailsVerified = true;
        this.updateAndRefreshStatusesColumns();
        this.loading = false;
      },
      error: e => {
        this.notificationService.error({
          title: 'Users',
          message: e.message ? e.message : 'An error verifying company details',
          requestMessage: e.statusText,
          requestCode: e.status,
          ts: e.timestamp ? e.timestamp : null,
        });
        this.loading = false;
      },
    });
  }

  private resendVerificationEmail(user: UserAdmin) {
    this.loading = true;
    this.service.resendVerificationEmail(user.id).subscribe({
      next: () => {
        this.notificationService.success('Verification email sent', 'Users');
        this.loading = false;
      },
      error: e => {
        this.notificationService.error({
          title: 'Users',
          message: e.message ? e.message : 'An error occurred while resending the email',
          requestMessage: e.statusText,
          requestCode: e.status,
          ts: e.timestamp ? e.timestamp : null,
        });
        this.loading = false;
      },
    });
  }

  private mfaDisable(user: UserAdmin) {
    this.loading = true;
    this.service.mfaDisableByAdmin(user.id).subscribe({
      next: () => {
        this.notificationService.success('2FA disabled', 'Users');
        this.loading = false;
        user.mfaEnabled = false;
      },
      error: e => {
        this.notificationService.error({
          title: 'Users',
          message: e.message ? e.message : 'An error occurred while 2FA disabling',
          requestMessage: e.statusText,
          requestCode: e.status,
          ts: e.timestamp ? e.timestamp : null,
        });
        this.loading = false;
      },
    });
  }

  private updateComment(user: UserAdmin, comment: string) {
    this.service
      .updateComment(user.id, comment)
      .pipe(
        catchError(e => {
          this.notificationService.error({
            title: 'Users',
            message: e.message ? e.message : 'An error occurred while updating comment',
            requestMessage: e.statusText,
            requestCode: e.status,
            ts: e.timestamp ? e.timestamp : null,
          });
          this.loading = false;
          return throwError(() => e);
        })
      )
      .subscribe(() => {
        this.notificationService.success('Comment updated', 'Users');
        this.loading = false;
        user.comment = comment;
        this.commentModalDialogRef.close();
      });
  }

  changeSize($event, size) {
    this.paginationPageSize = size;
    if (this.user) {
      this.localStorage.set(`users_table_component_size_${this.user.id}`, size);
    }
    this.update();
  }

  resetBatch() {
    this.batchSelectedIds.clear();
    this.gridApi.deselectAll();
  }

  update(spinner = true, afterUpdate?: () => void) {
    this.usersRequestSubscription?.unsubscribe();
    this.loading = spinner;

    this.requestParams.size = this.paginationPageSize;
    this.requestParams.page = this.currentPageNumber - 1;
    this.requestParams.sort = this.sortState;

    this.usersRequestSubscription = this.service.all(this.requestParams, this.userSearchObject).subscribe({
      next: res => {
        this.rowData = res.content;
        this.rowData.map(row => (row.emailVerified = row.emailVerified === '1' ? true : false));
        this.totalRowsCount = res.totalElements || 0;
        this.loading = false;
        this.gridApi?.setGridOption('rowData', this.rowData);
        this.restoreSelectionFromBatchIds();
        afterUpdate?.();
      },
      error: error => {
        this.loading = false;
        this.notificationService.error({
          title: 'Users',
          message: 'Loading users an error',
          serviceName: 'GATEWAY',
          requestMessage: error.statusText,
          requestCode: error.status,
        });
      },
    });
  }

  delete(user) {
    this.loading = true;
    this.service.delete(user.id).subscribe({
      next: () => {
        this.update();
        this.notificationService.success('User removed', 'Users');
      },
      error: () => {
        this.notificationService.error('Trying to remove user failed', 'Users');
        this.loading = false;
      },
    });
  }

  private getOwnerUsername(user: UserAdmin): string {
    if (user.ownerUserEmail) {
      return user.ownerUserEmail.split('@')[0];
    }
    return 'N/A';
  }

  private getAgreementStatusText(user: UserAdmin): string {
    if (user.role !== Role.MAIN) {
      return '';
    }

    const agreementStatusState = UserAgreementUtils.getMsaAndDpaAgreementState(user);

    return agreementStatusState.isAcceptedAgreement ? 'Yes, clickterm' : agreementStatusState.isCustomAgreement ? 'Yes, other' : 'Signature needed';
  }

  private renderAgreementStatus(user: UserAdmin): string {
    if (user.role !== Role.MAIN) {
      return '<div class="one-line"></div>';
    }

    const statusText = this.getAgreementStatusText(user);
    return `<div title='${statusText}' class="one-line">${statusText}</div>`;
  }

  private getIconForRole(user: UserAdmin): string {
    switch (user.role) {
      case Role.ADMIN:
        return '<i class="icon-admin"></i>';
      case Role.MAIN:
        return '<i class="icon-chess-king"></i>';
      case Role.DEPUTY:
        return '<i class="icon-chess-queen"></i>';
      case Role.SUB:
        return '<i class="icon-chess-pawn"></i>';
      default:
        return '';
    }
  }

  formatCurrency(value: UserAdmin): string {
    const currency = value != null ? this.currencyPipe.transform(value.balance, 'EUR', '', '1.2-2') : 'N';
    const isPostPaid = value.paymentType === 'POSTPAID';
    let formattedResult = '';
    if (currency?.[0] === '-') {
      formattedResult += `<span class="text-error-r3">${currency}</span>`;
    } else {
      formattedResult += `<span>${currency}</span>`;
    }
    if (isPostPaid) {
      formattedResult += `<i title="Postpaid customer" class="icon-postpaid"></i>`;
    }
    return formattedResult;
  }

  formatLastLogin(params: ICellRendererParams) {
    const lastActiveDate = params.data.lastActive;
    if (lastActiveDate) {
      const activeDate = moment(lastActiveDate).format('DD/MM/YY HH:mm:ss').split(' ');
      return `<div class="two-lines">${activeDate[1]} <br>${activeDate[0]}</div>`;
    }
    return `<div class="one-line">N/A</div>`;
  }

  formatNumericBoolean(n) {
    if (typeof n === 'boolean') {
      return n ? 'yes' : 'no';
    }
    return n === '1' ? 'yes' : 'no';
  }

  // ng grid
  getContextMenuItems = (params: GetContextMenuItemsParams): MenuItemDef[] => {
    const currentRowClicked = params.node?.data;
    const msaAndDpaAgreementState = UserAgreementUtils.getMsaAndDpaAgreementState(currentRowClicked);
    this.currentRow = currentRowClicked;

    const shouldShowCustomAgreementOnlyMenu =
      params.column?.getColId() === 'agreementStatus' &&
      currentRowClicked.role === Role.MAIN &&
      !currentRowClicked.customAgreement &&
      !currentRowClicked.clicktermAgreementFlowEnabled;

    if (shouldShowCustomAgreementOnlyMenu) {
      return [this.getEnableCustomAgreementMenuItem(currentRowClicked)];
    }

    let result: MenuItemDef[] = [
      {
        name: 'Enable ClickTerm Agreements',
        icon: '<span class="icon-doc fs-4 text-neutral-n8"></span>',
        action: () => {
          this.onAction({ name: 'enableClicktermAgreements', row: currentRowClicked, column: 'actions' });
        },
      },
      {
        name: 'Verify company details',
        icon: '<span class="icon-verify-company-details fs-4"></span>',
        action: () => {
          this.onAction({ name: 'verifyCompanyDetails', row: currentRowClicked, column: 'actions' });
        },
      },
      {
        name: 'Resend Verification Email',
        icon: '<span class="icon-refresh fs-4"></span>',
        action: () => {
          this.onAction({ name: 'resendVerificationEmail', row: currentRowClicked, column: 'actions' });
        },
      },
      {
        name: 'Disable HMAC OTP',
        icon: '<span class="icon-negative fs-4"></span>',
        action: () => {
          this.onAction({ name: 'mfaDisable', row: currentRowClicked, column: 'actions' });
        },
      },
      {
        name: 'Adjust balance',
        icon: '<span class="fas fa-coins fs-4"></span>',
        action: () => {
          this.onAction({ name: 'adjustBalance', row: currentRowClicked, column: 'actions' });
        },
      },
      {
        name: 'Download Agreement',
        icon: '<span class="icon-doc fs-4 text-neutral-n8"></span>',
        action: () => {
          this.onAction({ name: 'downloadAgreement', row: currentRowClicked, column: 'actions' });
        },
        disabled: this.isAgreementDownloading(),
      },
      {
        name: 'View Agreement',
        icon: '<span class="icon-certificate fs-4 text-neutral-n8"></span>',
        action: () => {
          this.onAction({ name: 'viewMsaDpaAgreement', row: currentRowClicked, column: 'actions' });
        },
      },
      {
        name: 'Edit user',
        icon: '<span class="icon-edit fs-4"></span>',
        action: () => {
          this.onAction({ name: 'edit', row: currentRowClicked, column: 'actions' });
        },
      },
      {
        name: 'Delete user',
        icon: '<span class="far fa-trash-alt fs-4"></span>',
        action: () => {
          this.onAction({ name: 'delete', row: currentRowClicked, column: 'actions' });
        },
      },
    ];
    if (currentRowClicked.companyDetailsVerified || currentRowClicked.role != Role.MAIN) {
      result = result.filter(item => item.name !== 'Verify company details');
    }
    if (currentRowClicked.role !== Role.MAIN || currentRowClicked.clicktermAgreementFlowEnabled || currentRowClicked.customAgreement) {
      result = result.filter(item => item.name !== 'Enable ClickTerm Agreements');
    }
    if (!(this.formatNumericBoolean(currentRowClicked.emailVerified) === 'no' && currentRowClicked.role === Role.MAIN)) {
      result = result.filter(item => item.name !== 'Resend Verification Email');
    }
    if (!currentRowClicked.mfaEnabled) {
      result = result.filter(item => item.name !== 'Disable 2FA');
    } else {
      result = result.filter(item => item.name !== 'Delete user');
    }
    if (!this.canAdjustBalance()) {
      result = result.filter(item => item.name !== 'Adjust balance');
    }
    if (!msaAndDpaAgreementState.canViewAcceptedAgreement) {
      result = result.filter(item => item.name !== 'Download Agreement');
      result = result.filter(item => item.name !== 'View Agreement');
    }
    return this.insertMenuItems(result, params, currentRowClicked);
  };

  insertMenuItems(result: MenuItemDef[], params: GetContextMenuItemsParams, currentRowClicked: UserAdmin): MenuItemDef[] {
    if (params.column.getColId() === 'username') {
      result.unshift(
        {
          name: 'Copy email',
          icon: '<span class="far fa-copy fs-4"></span>',
          action: () => {
            this.clipboard.copy(currentRowClicked.email);
          },
        },
        {
          name: 'Copy username',
          icon: '<span class="far fa-copy fs-4"></span>',
          action: () => {
            this.clipboard.copy(currentRowClicked.username);
          },
        }
      );
    }
    if (params.column.getColId() === 'companyName') {
      result.unshift({
        name: 'Copy company name',
        icon: '<span class="far fa-copy fs-4"></span>',
        action: () => {
          this.clipboard.copy(currentRowClicked.companyName);
        },
      });
    }
    if (params.column.getColId() === 'id') {
      result.unshift({
        name: 'Copy ID',
        icon: '<span class="far fa-copy fs-4"></span>',
        action: () => {
          this.clipboard.copy(currentRowClicked.id.toString());
        },
      });
    }
    return result;
  }

  private getEnableCustomAgreementMenuItem(currentRowClicked: UserAdmin): MenuItemDef {
    return {
      name: 'Yes, other (custom accepted)',
      icon: '<span class="icon-doc-custom fs-4 text-neutral-n8"></span>',
      action: () => {
        this.onAction({ name: 'enableCustomAgreement', row: currentRowClicked, column: 'agreementStatus' });
      },
    };
  }

  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    this.update();
    this.restoreColumnState();
  }

  private restoreColumnState(): void {
    if (!this.user) {
      return;
    }
    const columnState = this.localStorage.get(`users_table_state_${this.user.id}`);
    if (columnState?.length) {
      this.gridApi.applyColumnState({ state: columnState, applyOrder: true });
    }
  }

  onSelectionChanged(event: SelectionChangedEvent) {
    const selectedRows = event.api.getSelectedRows() as UserAdmin[];
    this.batchSelectedIds = new Set(selectedRows.map(row => row.id));
    this.changeBatch.emit(selectedRows);
  }

  // ng grid

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.actionSubscription.unsubscribe();
    this.usersRequestSubscription?.unsubscribe();
  }

  getRowId: GetRowIdFunc = (params: GetRowIdParams) => String(params.data.id);

  public onCommentSave(): void {
    this.loading = true;
    this.currentRow.comment = this.commentTextFormControl.value;
    this.updateComment(this.currentRow, this.currentRow.comment);
  }

  private downloadAgreement(row: UserAdmin): void {
    const template = row.clickwrapAgreementsStatuses.find(agreement => agreement.clickwrapTemplateId === environment.clicktermMsaDpaTemplateId);

    if (!template) {
      this.notificationService.error('Agreement was not found!', 'Legal Documents');
      return;
    }

    this.isAgreementDownloading.set(true);
    this.legalDocumentsService.downloadUserClickwrapAgreementByAdmin(row.id, environment.clicktermMsaDpaTemplateId).subscribe({
      next: blob => {
        BrowserUtils.downloadFile(blob, UserAgreementUtils.getMsaDpaAgreementDownloadFileName(row, template.lastStatusUpdate));
        this.isAgreementDownloading.set(false);
      },
      error: error => {
        if (error.status === 404) {
          this.notificationService.error('The agreement file was not found.', 'Users');
        } else {
          this.notificationService.error('There was a problem downloading the agreement. Please try again later.', 'Users');
        }
        this.isAgreementDownloading.set(false);
      },
    });
  }

  public getBalanceAdjustmentResult(): number {
    if (!this.currentRow) {
      return 0;
    }
    if (this.balanceAdjustmentForm.value.operation === 'SET_TO_ZERO') {
      return 0;
    }
    return this.currentRow.balance + this.getBalanceAdjustmentAmountMillis() / 1000;
  }

  public viewMsaDpaAgreement(row: UserAdmin): void {
    const template = row.clickwrapAgreementsStatuses.find(agreement => agreement.clickwrapTemplateId === environment.clicktermMsaDpaTemplateId);
    if (!template || !row.externalUserId) {
      this.notificationService.error('Agreement was not found!', 'Legal Documents');
      return;
    }

    if (!this.clicktermService.initialize()) {
      console.error('Clickterm dialog is not available.');
      this.notificationService.error('Cannot show accepted agreement. Please refresh the page and try again.', 'Users');
      return;
    }

    ClicktermDialog.showAcceptedContent({
      endUserId: row.externalUserId,
      clickwrapTemplateId: template.clickwrapTemplateId,
    })
      .then(_ => {
        console.log('Accepted content shown successfully.', _);
      })
      .catch(error => {
        console.error('Unable to show accepted content.', error);
        this.notificationService.error('There was a problem showing the accepted agreement. Please try again later.', 'Legal Documents');
      });
  }

  public isBalanceAdjustmentInvalid(): boolean {
    return this.balanceAdjustmentForm.value.operation === 'ADJUST' && this.getBalanceAdjustmentAmountMillis() === 0;
  }

  public onBalanceAdjustmentSave(): void {
    const operation = this.balanceAdjustmentForm.value.operation;
    if (operation === 'ADJUST' && this.isBalanceAdjustmentInvalid()) {
      return;
    }

    this.loading = true;
    const amountEuro = this.getBalanceAdjustmentAmountMillis() / 1000;
    const request = operation === 'SET_TO_ZERO' ? { operation: 'SET_TO_ZERO' as const } : { operation: 'ADJUST' as const, amountEuro: amountEuro };

    this.service.adjustBalanceByAdmin(this.currentRow.id, request).subscribe({
      next: () => {
        this.notificationService.success('Balance adjusted', 'Users');
        this.balanceAdjustmentModalDialogRef.close();
        this.update();
      },
      error: e => {
        this.notificationService.error({
          title: 'Users',
          message: e.message ? e.message : 'An error occurred while adjusting balance',
          requestMessage: e.statusText,
          requestCode: e.status,
          ts: e.timestamp ? e.timestamp : null,
        });
        this.loading = false;
      },
    });
  }

  private getBalanceAdjustmentAmount(): number {
    const value = this.balanceAdjustmentForm.value.amountEuro;
    const amount = typeof value === 'number' ? value : parseFloat(String(value));
    return Number.isFinite(amount) ? amount : 0;
  }

  private getBalanceAdjustmentAmountMillis(): number {
    const amount = this.getBalanceAdjustmentAmount();
    const absoluteMillis = Math.round(Math.abs(amount) * 1000);
    return amount < 0 ? -absoluteMillis : absoluteMillis;
  }

  onEnableClicktermAgreements(user: UserAdmin): void {
    if (this.loading || user.role !== Role.MAIN || user.clicktermAgreementFlowEnabled || user.customAgreement) {
      return;
    }

    this.modal
      .confirm(
        'Enable ClickTerm Agreements',
        `This will enable the ClickTerm agreement flow for user <b>${user.email}</b>. An agreement will be generated and sent to them once company details are verified. <br/>This action cannot be undone.`
      )
      .open()
      .result.then(result => {
        if (!result) {
          return;
        }
        this.updateClicktermAgreementSettings(user);
      });
  }

  onEnableCustomAgreement(user: UserAdmin): void {
    if (this.loading || user.role !== Role.MAIN || user.clicktermAgreementFlowEnabled || user.customAgreement) {
      return;
    }

    this.updateCustomAgreementSettings(user);
  }

  private updateClicktermAgreementSettings(user: UserAdmin): void {
    this.loading = true;
    this.service
      .updateClicktermAgreementSettings(user.id, {
        clicktermAgreementFlowEnabled: true,
      })
      .subscribe({
        next: () => {
          this.notificationService.success('Clickterm agreement settings updated', 'Users');
          user.clicktermAgreementFlowEnabled = true;
          this.updateAndRefreshStatusesColumns();
        },
        error: e => {
          this.notificationService.error({
            title: 'Users',
            message: e.message ? e.message : 'An error occurred while updating Clickterm agreement settings',
            requestMessage: e.statusText,
            requestCode: e.status,
            ts: e.timestamp ? e.timestamp : null,
          });
          this.loading = false;
        },
      });
  }

  private updateCustomAgreementSettings(user: UserAdmin): void {
    this.loading = true;
    this.service
      .updateClicktermAgreementSettings(user.id, {
        clicktermAgreementFlowEnabled: user.clicktermAgreementFlowEnabled,
        customAgreement: true,
      })
      .subscribe({
        next: () => {
          this.notificationService.success('Custom agreement set', 'Users');
          user.customAgreement = true;
          this.updateAndRefreshStatusesColumns();
        },
        error: e => {
          this.notificationService.error({
            title: 'Users',
            message: e.message ? e.message : 'An error occurred while updating custom agreement',
            requestMessage: e.statusText,
            requestCode: e.status,
            ts: e.timestamp ? e.timestamp : null,
          });
          this.loading = false;
        },
      });
  }

  private restoreSelectionFromBatchIds(): void {
    if (!this.gridApi) {
      return;
    }

    const availableIds = new Set(this.rowData.map(row => row.id));
    this.batchSelectedIds = new Set([...this.batchSelectedIds].filter(id => availableIds.has(id)));

    this.gridApi.forEachNode(node => {
      node.setSelected(this.batchSelectedIds.has(node.data?.id), false, 'checkboxSelected');
    });

    const selectedRows = this.gridApi.getSelectedRows() as UserAdmin[];
    this.batchSelectedIds = new Set(selectedRows.map(row => row.id));
    this.changeBatch.emit(selectedRows);
  }

  private updateAndRefreshStatusesColumns() {
    this.update(false, () => {
      this.gridApi.refreshCells({ force: true });
    });
  }
}
